import { FIL_URGENCES } from '@repere/core';
import type { Messages } from '@repere/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Alerte, Bouton, Champ, Invisible, Picto, Vide } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { maintenant } from '../horloge';
import { cleDuJour, dateComplete, heure } from './dates';
import { useTraduction } from '../i18n/langue';
import type { ParentDuDossier } from './types';

interface Message {
  id: string;
  caseFileId: string;
  sequence: string;
  body: string;
  thread: string | null;
  sentAt: string;
  authorId: string | null;
  hash: string;
  attachments: {
    id: string;
    /** Nom de remise, extension comprise : l'API le résout, l'interface s'y fie. */
    fileName: string;
    fileHash: string;
    contentType: string;
    sizeBytes: number;
  }[];
}

interface MessageProgramme {
  id: string;
  scheduledFor: string;
  thread: string | null;
  body: string;
  /** Ce qui partira avec : un envoi différé se relit et s'annule, il ne se corrige pas. */
  attachments: { fileName: string; contentType: string; sizeBytes: number }[];
}

/**
 * Le fil est une **navigation**, pas un champ du formulaire.
 *
 * Il était auparavant un menu déroulant posé sous la zone de saisie : on ne pouvait le
 * choisir qu'en écrivant, et jamais s'en servir pour lire — impossible de relire « tout
 * École ». La même rangée fait désormais les deux, et c'est ce qui la rend justifiable :
 * un seul geste, un seul état, pas de second choix à faire au moment d'envoyer.
 *
 * `TOUS` n'est pas un fil, c'est l'absence de filtre : on y lit tout, et ce qu'on y écrit
 * part sans fil — comme avant. Il se distingue de « Sans fil », qui est un vrai fil, celui
 * des messages qu'on n'a rangés nulle part.
 */
const TOUS = 'tous';

/**
 * Les fils, dans leur forme **canonique** — celle qui part au serveur et qui y est déjà
 * stockée pour tout message passé. Le libellé, lui, se traduit ; la valeur ne bouge pas
 * avec la langue, pour la même raison que les catégories de dépense : deux parents qui
 * lisent le même dossier dans deux langues doivent voir les mêmes fils.
 */
function fils(m: Messages['dossier']['messagerie']): { valeur: string; libelle: string }[] {
  return [
    { valeur: TOUS, libelle: m.filTous },
    { valeur: '', libelle: m.filSansFil },
    { valeur: 'sante', libelle: m.filSante },
    { valeur: 'ecole', libelle: m.filEcole },
    { valeur: 'vacances', libelle: m.filVacances },
    // Le seul fil que le produit alimente lui-même : au-delà de deux urgences par 24 h,
    // l'alerte cesse d'en être une et atterrit ici (docs/02 §4 bis). Il reste un fil
    // ordinaire — on peut y écrire, y répondre, et le relire comme les autres.
    { valeur: FIL_URGENCES, libelle: m.filUrgences },
  ];
}

/** Le fil d'un message, ramené à la valeur employée par les onglets. */
const filDe = (message: { thread: string | null }): string => message.thread ?? '';

/**
 * Le libellé lisible d'un fil. Le message porte sa clé — « ecole » — et l'afficher telle
 * quelle mettait un mot sans accent ni majuscule à côté d'un onglet qui dit « École ».
 * Une clé inconnue, venue d'un fil retiré depuis, s'affiche telle quelle plutôt que de
 * disparaître : mieux vaut un mot brut qu'un message qui semble n'avoir jamais eu de fil.
 */
const libelleDe = (valeur: string, options: { valeur: string; libelle: string }[]): string =>
  options.find((option) => option.valeur === valeur)?.libelle ?? valeur;

/**
 * Messagerie du dossier.
 *
 * Ce qui n'y figure pas est aussi important que ce qui y figure : aucun accusé de
 * lecture, aucun statut de présence, aucun indicateur de frappe. La remise en temps réel
 * est un raccourci de transport, et l'interface ne laisse jamais deviner si l'autre parent
 * est connecté (docs/01 §5.1 et §5.2).
 *
 * L'immuabilité est annoncée avant le premier envoi, pas enfouie dans des conditions
 * générales : c'est la promesse du produit, et elle change la façon d'écrire.
 */
export function Messagerie({
  caseFileId,
  parents,
  monUserId,
  messagerieActive,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
  monUserId: string | null;
  messagerieActive: boolean;
}) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.dossier.messagerie;
  const optionsFils = fils(m);
  const queryClient = useQueryClient();
  const [texte, setTexte] = useState('');
  const [fil, setFil] = useState<string>(TOUS);
  const [differe, setDiffere] = useState(false);
  const [quand, setQuand] = useState('');
  const [piece, setPiece] = useState<File | null>(null);
  const [erreurPiece, setErreurPiece] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(0);
  const zone = useRef<HTMLOListElement>(null);
  const auBas = useRef(true);
  const compteConnu = useRef(0);

  /**
   * L'immuabilité tenait un bandeau ambré pleine largeur, entre les fils et la
   * conversation — sur téléphone, l'essentiel de ce qu'on venait lire pour compter deux
   * écrans de défilement. Elle se lit toujours **avant d'écrire**, pas ailleurs (docs/01
   * §5.5), mais une bille d'information reste plus proche de la conversation qu'un
   * paragraphe qu'on ne relit qu'une fois.
   */
  const [infoImmuableOuverte, setInfoImmuableOuverte] = useState(false);
  const infoImmuableBloc = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!infoImmuableOuverte) return;
    const auClic = (evenement: MouseEvent) => {
      if (!infoImmuableBloc.current?.contains(evenement.target as Node)) setInfoImmuableOuverte(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setInfoImmuableOuverte(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [infoImmuableOuverte]);

  // Le fil de lecture est aussi celui d'écriture — sauf « Tous », qui n'est pas un fil.
  const filSortant = fil === TOUS ? '' : fil;

  const messagesQuery = useQuery({
    queryKey: ['messages', caseFileId],
    queryFn: () => client.request<Message[]>(`/case-files/${caseFileId}/messages`),
    enabled: messagerieActive,
  });

  const programmes = useQuery({
    queryKey: ['messages-programmes', caseFileId],
    queryFn: () =>
      client.request<MessageProgramme[]>(`/case-files/${caseFileId}/messages/scheduled`),
    enabled: messagerieActive,
  });

  // Le flux temps réel est ouvert une fois pour tout le dossier (voir `Dossier.tsx`) : il
  // rafraîchit aussi bien cette liste que les pastilles des autres catégories. L'ouvrir
  // ici en plus ferait deux connexions par dossier pour le même signal. Sa présence ou son
  // absence n'est jamais montrée, et la relève au chargement suffit à tout voir.

  /** Ramène la conversation en bas et éteint la pastille d'arrivée. */
  const descendre = useCallback(() => {
    const element = zone.current;
    // Affectation plutôt que `scrollTo` : celui-ci n'existe pas partout, et une
    // conversation qui ne défile pas vaut mieux qu'un écran qui ne s'affiche pas.
    if (element) element.scrollTop = element.scrollHeight;
    auBas.current = true;
    setEnAttente(0);
  }, []);

  /**
   * L'arrivée d'un message ne déplace la vue que si on était déjà en bas.
   *
   * Sinon elle s'annonce par une pastille. Faire défiler quelqu'un qui relit un échange
   * plus haut lui fait perdre sa ligne — et sur cet écran-là, ce qu'on relit est souvent
   * ce dont on discute.
   */
  useEffect(() => {
    const total = messagesQuery.data?.length ?? 0;
    const arrives = total - compteConnu.current;
    compteConnu.current = total;
    if (arrives <= 0) return;
    if (auBas.current) descendre();
    else setEnAttente((deja) => deja + arrives);
  }, [messagesQuery.data?.length, descendre]);

  // Changer de fil rebat la conversation : on repart du bas, comme à l'ouverture.
  useEffect(() => {
    descendre();
  }, [fil, descendre]);

  const surDefilement = (): void => {
    const element = zone.current;
    if (!element) return;
    // 48 px de tolérance : on est « en bas » dès qu'il ne reste qu'une ligne à voir.
    auBas.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
    if (auBas.current) setEnAttente(0);
  };

  /**
   * La reformulation proposée, ou `null` — l'état par défaut, de loin le plus fréquent.
   *
   * Elle vit dans l'écran et **nulle part ailleurs** : ni en base, ni dans le cache de
   * requêtes. Rien de ce parcours ne survit à la fermeture de l'onglet, et c'est la
   * quatrième règle du dispositif — l'autre parent ne doit pas pouvoir apprendre que
   * celui-ci a fait relire son brouillon.
   */
  const [proposition, setProposition] = useState<string | null>(null);

  /**
   * Demande une autre version du brouillon.
   *
   * `motif` distingue ce qui ne dépend pas du parent — clé absente, panne, délai — de son
   * enveloppe du jour épuisée : l'écran peut proposer d'attendre demain dans un cas, et ne
   * rien promettre dans l'autre. Un brouillon déjà factuel rend `null` sans motif : il n'y
   * avait rien à adoucir, et afficher deux fois le même texte côte à côte le dirait mal.
   */
  const reformuler = useMutation({
    mutationFn: () =>
      client.request<{ proposition: string | null; motif?: string }>(
        `/case-files/${caseFileId}/messages/reformulate`,
        { method: 'POST', body: { body: texte } },
      ),
    onSuccess: (resultat) => setProposition(resultat.proposition),
  });

  const envoyer = useMutation({
    mutationFn: async () => {
      setErreurPiece(null);

      // La pièce part **avant** le message : analysée, typée et chiffrée, elle est
      // acceptée ou refusée pendant que rien n'est encore entré dans l'historique. Un
      // message envoyé puis amputé de sa pièce serait inexplicable — et inaltérable.
      let jointe:
        | {
            storageKey: string;
            fileName: string;
            fileHash: string;
            contentType: string;
            sizeBytes: number;
          }
        | undefined;

      if (piece) {
        const formulaire = new FormData();
        formulaire.append('file', piece);
        const depose = await client.request<{
          storageKey: string;
          fileName: string;
          fileHash: string;
          contentType: string;
          sizeBytes: number;
        }>(`/case-files/${caseFileId}/messages/attachments`, {
          method: 'POST',
          form: formulaire,
        });
        jointe = {
          storageKey: depose.storageKey,
          // Le nom vient du serveur, pas du `File` local : c'est lui qui l'a assaini, et
          // c'est sous ce nom-là que la pièce sera remise à l'autre parent.
          fileName: depose.fileName,
          fileHash: depose.fileHash,
          contentType: depose.contentType,
          sizeBytes: depose.sizeBytes,
        };
      }

      const corps = {
        body: texte,
        ...(filSortant ? { thread: filSortant } : {}),
        ...(jointe ? { attachments: [jointe] } : {}),
      };

      return differe
        ? client.request(`/case-files/${caseFileId}/messages/scheduled`, {
            method: 'POST',
            body: { ...corps, scheduledFor: new Date(quand).toISOString() },
          })
        : client.request(`/case-files/${caseFileId}/messages`, { method: 'POST', body: corps });
    },
    onError: (cause) => setErreurPiece(messageDErreur(cause)),
    onSuccess: () => {
      setTexte('');
      setQuand('');
      setPiece(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', caseFileId] });
      void queryClient.invalidateQueries({ queryKey: ['messages-programmes', caseFileId] });
    },
  });

  const annuler = useMutation({
    mutationFn: (id: string) =>
      client.request(`/case-files/${caseFileId}/messages/scheduled/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['messages-programmes', caseFileId] }),
  });

  if (!messagerieActive) {
    return (
      <p className="discret" style={{ margin: 0 }}>
        {m.inactiveTexte}
      </p>
    );
  }

  /**
   * Enregistre une pièce jointe, par un lien signé de courte durée.
   *
   * Le lien est demandé au moment du clic, jamais posé d'avance dans le document : une
   * adresse valable cinq minutes qui traîne dans le HTML d'une page ouverte depuis une
   * heure n'aurait servi qu'à échouer.
   *
   * Le fichier est **rapatrié puis enregistré**, et non ouvert dans un onglet. Deux
   * raisons, et chacune suffisait à empêcher d'ouvrir quoi que ce soit :
   *
   * 1. `window.open` appelé après un `await` n'est plus rattaché au clic : les
   *    navigateurs le comptent comme une fenêtre surgissante et le bloquent, sans rien
   *    dire. Un `blob:` fabriqué ici ne passe pas par là.
   * 2. Le lien vise l'API, qui n'est pas sur l'origine de l'application. Le rapatrier
   *    rend l'échec lisible — expiré, retiré du dossier, empreinte cassée — au lieu de
   *    laisser un onglet blanc ou une page d'erreur JSON.
   */
  const enregistrerPiece = async (attachmentId: string, nomFichier: string): Promise<void> => {
    const lien = await client.request<{ url: string }>(
      `/attachments/${attachmentId}/download-link`,
      { method: 'POST' },
    );

    const fichier = await client.telechargerLienSigne(lien.url);

    const adresse = URL.createObjectURL(fichier.bytes);
    const ancre = document.createElement('a');
    ancre.href = adresse;
    ancre.download = nomFichier;
    document.body.append(ancre);
    ancre.click();
    ancre.remove();
    // Sans cette libération, le fichier reste en mémoire jusqu'au rechargement de l'onglet.
    URL.revokeObjectURL(adresse);
  };

  const ouvrirPiece = useMutation({
    mutationFn: ({ id, nomFichier }: { id: string; nomFichier: string }) =>
      enregistrerPiece(id, nomFichier),
  });

  const nomDe = (authorId: string | null): string => {
    if (authorId === monUserId) return m.auteurVous;
    const auteur = parents.find((p) => !p.isViewer);
    return auteur?.displayName ?? m.auteurAutreParent;
  };

  const tous = messagesQuery.data ?? [];
  const visibles = fil === TOUS ? tous : tous.filter((message) => filDe(message) === fil);

  // Le compte par fil dit où la conversation a lieu, et quels fils sont restés vides.
  // « Tous » n'en porte pas : il ne compterait que le total, que la conversation affiche déjà.
  const compteDuFil = (valeur: string): number =>
    valeur === TOUS ? 0 : tous.filter((message) => filDe(message) === valeur).length;

  return (
    <>
      <div className="filtres" role="group" aria-label={m.ariaFils}>
        {optionsFils.map((option) => {
          const actif = option.valeur === fil;
          const compte = compteDuFil(option.valeur);
          return (
            <button
              key={option.valeur || 'sans-fil'}
              type="button"
              className={actif ? 'filtre-onglet actif' : 'filtre-onglet'}
              aria-pressed={actif}
              onClick={() => setFil(option.valeur)}
            >
              {option.libelle}
              {compte > 0 ? (
                <>
                  <span className="filtre-compte" aria-hidden="true">
                    {compte > 99 ? '99+' : compte}
                  </span>
                  <Invisible>{m.messageSuffixe(compte)}</Invisible>
                </>
              ) : null}
            </button>
          );
        })}
      </div>

      {messagesQuery.isError ? (
        <Alerte titre={m.erreurChargement}>{messageDErreur(messagesQuery.error)}</Alerte>
      ) : null}

      {/* La conversation a son propre cadre et son propre défilement : c'est ce qui lui
          donne une place stable, où l'on sait que les messages arrivent. La bille
          d'information vit dans son coin plutôt que sur sa propre rangée : elle nommait
          un geste rare — on ne la rouvre pas à chaque message — et une rangée entière
          pour un seul geste rare volait sa hauteur à ce qu'on vient lire. */}
      <div className="conversation">
        <div className="info-bulle" ref={infoImmuableBloc}>
          <button
            type="button"
            className="info-bulle-bouton"
            aria-expanded={infoImmuableOuverte}
            aria-controls="mention-immuable-texte"
            onClick={() => setInfoImmuableOuverte((etat) => !etat)}
          >
            <span aria-hidden="true">i</span>
            <Invisible>{m.mentionImmuableBouton}</Invisible>
          </button>
          {infoImmuableOuverte ? (
            <p id="mention-immuable-texte" className="mention-immuable info-bulle-texte">
              {m.mentionImmuable}
            </p>
          ) : null}
        </div>

        <ol className="fil-messages" ref={zone} onScroll={surDefilement}>
          {visibles.map((message, index) => {
            const deMoi = message.authorId === monUserId;
            const envoye = new Date(message.sentAt);
            const jour = cleDuJour(envoye);
            const precedent = visibles[index - 1];
            const nouveauJour = !precedent || cleDuJour(new Date(precedent.sentAt)) !== jour;

            return (
              <Fragment key={message.id}>
                {nouveauJour ? (
                  <li className="fil-jour" aria-hidden="true">
                    <span>{dateComplete(envoye, locale)}</span>
                  </li>
                ) : null}
                <li className={deMoi ? 'message de-moi' : 'message de-autre'}>
                  <div className="bulle">
                    <p className="entete">
                      <strong>{nomDe(message.authorId)}</strong>{' '}
                      <span className="discret">
                        {heure(envoye, locale)}
                        {message.thread ? ` · ${libelleDe(message.thread, optionsFils)}` : ''}
                      </span>
                    </p>
                    <p className="corps">{message.body}</p>
                    {message.attachments.length > 0 ? (
                      <p className="discret" style={{ marginBottom: 0 }}>
                        {message.attachments.map((jointe) => (
                          <Bouton
                            key={jointe.id}
                            variante="secondaire"
                            // Le nom du fichier plutôt qu'un libellé générique : c'est lui
                            // qui dit ce qu'on s'apprête à ouvrir, et sous quel nom il
                            // sera enregistré.
                            aria-label={`${m.ouvrirLaPieceJointe} : ${jointe.fileName}`}
                            disabled={ouvrirPiece.isPending}
                            onClick={() =>
                              ouvrirPiece.mutate({ id: jointe.id, nomFichier: jointe.fileName })
                            }
                          >
                            {jointe.fileName}
                          </Bouton>
                        ))}{' '}
                        {m.pieceJointeScellee}
                      </p>
                    ) : null}
                    {/* La date complète part au lecteur d'écran : le séparateur de jour
                        qui la porte à l'écran lui est masqué. */}
                    <Invisible>
                      {m.messageInvisible(dateComplete(envoye, locale), heure(envoye, locale), message.sequence)}
                    </Invisible>
                  </div>
                </li>
              </Fragment>
            );
          })}

          {visibles.length === 0 ? (
            <li className="fil-vide">
              {tous.length === 0 ? (
                <Vide categorie="messages">{m.videAucun}</Vide>
              ) : (
                <Vide categorie="messages">{m.videAucunDansCeFil}</Vide>
              )}
            </li>
          ) : null}
        </ol>

        {enAttente > 0 ? (
          <button type="button" className="fil-arrivee" onClick={descendre}>
            {m.nouveauxMessages(enAttente)}
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M10.6 3.6 Q10.6 2.4 12 2.4 Q13.4 2.4 13.4 3.6 L13.4 15.3 L17.4 11.6 Q18.4 10.7 19.3 11.7 Q20.2 12.7 19.2 13.6 L12.9 19.5 Q12 20.3 11.1 19.5 L4.8 13.6 Q3.8 12.7 4.7 11.7 Q5.6 10.7 6.6 11.6 L10.6 15.3 Z"
              />
            </svg>
          </button>
        ) : null}
      </div>

      {/* Un échec d'ouverture se dit. Il était jusqu'ici avalé : le clic ne produisait
          rien, et rien n'expliquait pourquoi. */}
      {ouvrirPiece.isError ? (
        <Alerte titre={m.erreurOuverturePiece}>{messageDErreur(ouvrirPiece.error)}</Alerte>
      ) : null}

      {(programmes.data ?? []).length > 0 ? (
        <section aria-labelledby="programmes-titre">
          <h3 id="programmes-titre">{m.vosEnvoisDifferes}</h3>
          <p className="discret">{m.envoisDifferesAide}</p>
          <ul>
            {programmes.data?.map((programme) => (
              <li key={programme.id}>
                {m.envoiPrevu(
                  programme.body,
                  dateComplete(new Date(programme.scheduledFor), locale),
                  heure(new Date(programme.scheduledFor), locale),
                )}{' '}
                {programme.attachments.length > 0
                  ? `(${programme.attachments.map((jointe) => jointe.fileName).join(', ')}) `
                  : null}
                <Bouton variante="secondaire" onClick={() => annuler.mutate(programme.id)}>
                  {m.annulerCetEnvoi}
                </Bouton>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* La barre de saisie tient sur deux rangées. Le différé et la pièce jointe existaient
          en permanence, avec leur paragraphe d'aide : quatre champs dépliés en permanence
          pour deux gestes rares, qui repoussaient la conversation hors de vue. Les trois
          outils occupent leur propre rangée pour que le champ de texte tienne, seul avec
          l'envoi, toute la largeur de l'écran. */}
      <form
        className="redaction"
        onSubmit={(e) => {
          e.preventDefault();
          envoyer.mutate();
        }}
      >
        <div className="redaction-outils">
          <label
            className={piece ? 'redaction-outil actif' : 'redaction-outil'}
            title={m.joindreUnDocument}
          >
            <Picto categorie="journal" />
            <Invisible>{m.joindreUnDocument}</Invisible>
            <input
              id="message-piece"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => setPiece(e.target.files?.[0] ?? null)}
            />
          </label>

          <button
            type="button"
            className={differe ? 'redaction-outil actif' : 'redaction-outil'}
            aria-pressed={differe}
            onClick={() => setDiffere(!differe)}
            title={m.envoyerPlusTard}
          >
            <Picto categorie="aujourdhui" />
            <Invisible>{m.envoyerPlusTard}</Invisible>
          </button>

          <button
            type="button"
            className="redaction-outil"
            onClick={() => reformuler.mutate()}
            disabled={!texte.trim() || reformuler.isPending}
            title={m.reformuler}
          >
            <Picto categorie="messages" />
            <Invisible>{m.reformuler}</Invisible>
          </button>
        </div>

        <div className="redaction-envoi">
          <label htmlFor="message-corps">
            <Invisible>{m.votreMessage}</Invisible>
          </label>
          <textarea
            id="message-corps"
            rows={2}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            maxLength={10_000}
            placeholder={m.placeholderEcrire}
            required
          />

          <button
            type="submit"
            className="redaction-envoyer"
            disabled={!texte.trim() || (differe && !quand) || envoyer.isPending}
            title={envoyer.isPending ? m.envoiEnCours : differe ? m.programmerLEnvoi : m.envoyer}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M2 21 L23 12 L2 3 L2 10 L17 12 L2 14 Z" />
            </svg>
            <Invisible>
              {envoyer.isPending ? m.envoiEnCours : differe ? m.programmerLEnvoi : m.envoyer}
            </Invisible>
          </button>
        </div>

        {differe ? (
          <div className="redaction-repli">
            <Champ
              label={m.dateEtHeureEnvoi}
              type="datetime-local"
              value={quand}
              onChange={(e) => setQuand(e.target.value)}
              min={new Date(maintenant().getTime() + 60_000).toISOString().slice(0, 16)}
              aide={m.aideHorodatage}
              required
            />
          </div>
        ) : null}

        {piece ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {m.redactionPieceScellee(piece.name)}{' '}
              <Bouton variante="lien" onClick={() => setPiece(null)}>
                {m.retirer}
              </Bouton>
            </p>
          </div>
        ) : null}

        {/* La proposition, sous la barre de saisie et jamais à sa place : le brouillon
            reste à l'écran, intact, tant que le parent n'a pas choisi. L'IA propose, elle
            ne remplace pas — et l'envoi n'a jamais été conditionné à ce détour. */}
        {reformuler.isPending ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {m.reformulationEnCours}
            </p>
          </div>
        ) : null}

        {proposition ? (
          <div className="redaction-repli reformulation">
            <p className="reformulation-titre">{m.reformulationTitre}</p>
            <p className="reformulation-texte">{proposition}</p>
            <p>
              <Bouton
                onClick={() => {
                  setTexte(proposition);
                  setProposition(null);
                }}
              >
                {m.reformulationUtiliser}
              </Bouton>{' '}
              <Bouton variante="secondaire" onClick={() => setProposition(null)}>
                {m.reformulationGarder}
              </Bouton>
            </p>
            <p className="discret" style={{ marginBottom: 0 }}>
              {m.reformulationAide}
            </p>
          </div>
        ) : null}

        {reformuler.isSuccess && !proposition && !reformuler.isPending ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {reformuler.data?.motif === 'budget'
                ? m.reformulationBudget
                : reformuler.data?.motif === 'trop_long'
                  ? m.reformulationTropLong
                  : reformuler.data?.motif
                    ? m.reformulationIndisponible
                    : m.reformulationDejaNeutre}
            </p>
          </div>
        ) : null}

        {reformuler.isError ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {m.reformulationIndisponible}
            </p>
          </div>
        ) : null}

        {envoyer.isError ? (
          <Alerte titre={m.erreurEnvoi}>{erreurPiece ?? messageDErreur(envoyer.error)}</Alerte>
        ) : null}
      </form>
    </>
  );
}
